import PortalIdParser from 'PortalIdParser';
import { reportError } from 'automation-ui-core/errors/utils/reportError';
import intersection from 'hs-lodash/intersection';
import { List, OrderedSet, Map as ImmutableMap } from 'immutable';
import get from 'immutable-less/transmute/get';
import getIn from 'immutable-less/transmute/getIn';
import * as ApiPatchUpdateTypes from 'automation-ui-canvas/flow/constants/ApiPatchUpdateTypes';
import * as EnrollmentFilterBranchTypes from 'automation-ui-enrollment/constants/EnrollmentFilterBranchTypes';
import { hydrateMetadata } from 'automation-ui-action-features/actions/ActionMetadataConfigUtils';
import Action from 'automation-ui-action-features/records/Action';
import { createActionCardCounters as newCreateActionCardCounters, createActionIdMap as newCreateActionIdMap, findAllAncestorsAndDecendantsOfAction as newFindAllAncestorsAndDecendantsOfAction, findAllDecendantsActionIds as newFindAllDecendantsActionIds, getActionHeaderTitle as newGetActionHeaderTitle, getActionOutputsForTargetActionById as newGetActionOutputsForTargetActionById } from 'automation-ui-action-features/records/recordsUtils';
import ClassicEnrollmentSettings, { ClassicEnrollmentSettingsKeys } from 'automation-ui-canvas/flow/records/properties/ClassicEnrollmentSettings';
import ReenrollmentFilter from 'automation-ui-enrollment/records/ReenrollmentFilter';
import { checkFilterEquality, hasFilters, isPersistedReenrollmentListOperator } from 'automation-ui-enrollment/utils/filterUtils';
import { NEW_ACTION } from 'automation-ui-flow-core/canvas/SpecialActionIds';
import * as ListTypes from 'automation-ui-flow-core/flow/ListTypes';
import { BLOCKLISTED_PLATFORM_SOURCE_APPS } from 'automation-ui-source-apps/constants/WorkflowsAppSupportedSourceApps';
import EnrollmentSchedule from 'automation-ui-enrollment/records/EnrollmentSchedule';
import { fetchFixedDataPassingReferences } from 'automation-ui-canvas/data/api/cloneActionApis';
import { isFilterEmpty } from 'automation-ui-flow-core/enrollment/enrollmentUtils';

// Signifies that we should come back and type this value once upstream modules are typed

export const getId = get('flowId');
export const getActions = get('actions');
export const getName = get('name');
export const getVersion = get('version');
export const getUuid = get('uuid');
export const getIsEnabled = get('isEnabled');
export const getObjectTypeId = get('objectTypeId');
export const getNextAvailableActionId = get('nextAvailableActionId');
export const getFirstActionId = get('firstActionId');
export const getFlowEventFilters = get('flowEventFilters');
export const getAssociatedLists = get('associatedLists');
export const getCreateMetadata = get('createMetadata');
export const getUpdateMetadata = get('updateMetadata');
export const getShouldReenroll = get('shouldReenroll');
export const getIsClassicWorkflow = get('isClassicWorkflow');
export const getTimeWindow = get('timeWindow');
export const getEnrollmentSchedule = get('enrollmentSchedule');
export const hasEnrollmentSchedule = flow => {
  return flow && flow.hasEnrollmentSchedule();
};
export const getTimeWindowRanges = getIn(['timeWindow', 'timeRanges']);
export const getClassicEnrollmentSettings = get('classicEnrollmentSettings');
export const getClassicReEnrollmentSettings = getIn(['classicEnrollmentSettings', ClassicEnrollmentSettingsKeys.REENROLLMENT_TRIGGER_SETS]);
export const getClassicEventAnchor = getIn(['classicEnrollmentSettings', ClassicEnrollmentSettingsKeys.EVENT_ANCHOR]);
export const getClassicRecurAnnually = getIn(['classicEnrollmentSettings', ClassicEnrollmentSettingsKeys.RECUR_ANNUALLY]);
export const getClassicCanEnrollFromSalesforce = getIn(['classicEnrollmentSettings', ClassicEnrollmentSettingsKeys.CAN_ENROLL_FROM_SALESFORCE]);
export const getWorkflowId = getIn(['classicEnrollmentSettings', ClassicEnrollmentSettingsKeys.WORKFLOW_ID]);
export const getClassicGoalCriteria = getIn(['classicEnrollmentSettings', ClassicEnrollmentSettingsKeys.GOAL_CRITERIA]);
export const getClassicListening = getIn(['classicEnrollmentSettings', ClassicEnrollmentSettingsKeys.LISTENING]);
export const getClassicSuppressionListIds = getIn(['classicEnrollmentSettings', ClassicEnrollmentSettingsKeys.SUPPRESSION_LIST_IDS]);
export const getClassicUnenrollmentSetting = getIn(['classicEnrollmentSettings', ClassicEnrollmentSettingsKeys.UNENROLLMENT_SETTING]);
export const getInternalStartingListId = getIn(['classicEnrollmentSettings', ClassicEnrollmentSettingsKeys.INTERNAL_STARTING_LIST_ID]);
export const getDescription = getIn(['classicEnrollmentSettings', ClassicEnrollmentSettingsKeys.DESCRIPTION]);
export const getAllowEnrollmentFromMerge = getIn(['classicEnrollmentSettings', ClassicEnrollmentSettingsKeys.ALLOW_ENROLLMENT_FROM_MERGE]);
export const getActionById = (flow, actionId) => getIn(['actions', actionId], flow);
export const getHasNoActions = flow => {
  const actions = getActions(flow);
  return !actions || actions.size === 0;
};

// For more information re: PERSISTED_RE_ENROLLMENT_LIST,
// see https://git.hubteam.com/HubSpot/WorkflowsReactUI/pull/3339#issuecomment-1181225
export const getReenrollmentListType = filter => isPersistedReenrollmentListOperator(filter) ? ListTypes.PERSISTED_RE_ENROLLMENT_LIST : ListTypes.RE_ENROLLMENT_LIST;
export const getAssociatedListsUpdates = ({
  existingLists,
  updatedLists,
  listTypes
}) => {
  const existingListsSet = (existingLists || List()).map(list => list.listId).toSet();
  const newListsSet = (updatedLists || List()).map(list => list.listId).toSet();
  const listsToDelete = existingListsSet.subtract(newListsSet);
  const listsToAdd = newListsSet.subtract(existingListsSet);
  const listUpdates = [];
  listsToDelete.forEach(listId => {
    listUpdates.push({
      listId,
      listTypes,
      updateType: ApiPatchUpdateTypes.DELETE
    });
  });
  listsToAdd.forEach(listId => {
    listUpdates.push({
      listId,
      listTypes,
      updateType: ApiPatchUpdateTypes.CREATE
    });
  });
  return listUpdates;
};
export const getAssociatedListUpdate = ({
  existingList,
  updatedFilters,
  listTypes
}) => {
  const listUpdate = {
    filterBranch: updatedFilters,
    listTypes
  };
  if (existingList && isFilterEmpty(updatedFilters)) {
    listUpdate.listId = existingList.listId;
    listUpdate.updateType = ApiPatchUpdateTypes.DELETE;
    listUpdate.filterBranch = null;
  } else if (existingList && !isFilterEmpty(updatedFilters)) {
    listUpdate.listId = existingList.listId;
    listUpdate.updateType = ApiPatchUpdateTypes.UPDATE;
  } else if (!isFilterEmpty(updatedFilters)) {
    listUpdate.updateType = ApiPatchUpdateTypes.CREATE;
  }
  return listUpdate;
};
export const getAssociatedListByType = listType => {
  return flow => {
    const associatedLists = getAssociatedLists(flow);
    if (associatedLists) {
      const associatedListsByType = associatedLists.filter(list => list.listTypes.includes(listType));
      const listCount = associatedListsByType.size;
      if (listCount) {
        if (listCount > 1) {
          reportError({
            error: new Error('getAssociatedListByType expects one AssociatedList of the given type, but found more than one'),
            extraData: {
              associatedListsByType
            }
          });
        }
        return associatedListsByType.first();
      }
    }
    return null;
  };
};
export const getAssociatedListsByType = (...listTypes) => {
  return flow => {
    const associatedLists = getAssociatedLists(flow);
    if (associatedLists) {
      return associatedLists.filter(list => intersection(list.listTypes, listTypes).length > 0);
    }
    return null;
  };
};
export const getEnrollmentList = getAssociatedListByType(ListTypes.ENROLLMENT_LIST);
export const getSuppressionList = getAssociatedListByType(ListTypes.SUPPRESSION_LIST);
export const getSuppressionLists = getAssociatedListsByType(ListTypes.SUPPRESSION_LIST);
export const getGoalsList = getAssociatedListByType(ListTypes.CLASSIC_GOAL_LIST);
export const getReenrollmentLists = getAssociatedListsByType(ListTypes.RE_ENROLLMENT_LIST, ListTypes.PERSISTED_RE_ENROLLMENT_LIST);
export const getEnrollmentFilters = flow => {
  const list = getEnrollmentList(flow);
  return list ? list.filterBranch : null;
};
export const getSuppressionFilters = flow => {
  const list = getSuppressionList(flow);
  return list ? list.filterBranch : null;
};
export const getGoalFilters = flow => {
  const list = getGoalsList(flow);
  return list ? list.filterBranch : null;
};
export const getReenrollmentFilters = flow => {
  const lists = getReenrollmentLists(flow);
  if (lists) {
    return OrderedSet(lists.map(list => getIn(['filterBranch', 'filters'], list) && list.filterBranch.filters.length > 0 ? Object.assign({}, list.filterBranch.filters[0]) : null).filter(reenrollmentFilter => !!reenrollmentFilter).map(filter => new ReenrollmentFilter(filter)));
  }
  return OrderedSet();
};
export const getSegmentCriteria = flow => {
  return getIn(['classicEnrollmentSettings', ClassicEnrollmentSettingsKeys.SEGMENT_CRITERIA], flow);
};
export const getTriggers = flow => {
  const enrollmentCriteria = flow.enrollmentCriteria;
  if (!enrollmentCriteria || enrollmentCriteria.triggerType !== 'EVENT') {
    return undefined;
  }
  return enrollmentCriteria.triggers;
};
export const getTemplateMetadata = flow => {
  return get('templateMetadata', getCreateMetadata(flow)) || null;
};
export function buildAssociatedListFromFilter(filter, parentObjectTypeId) {
  const {
    objectTypeId
  } = filter;
  const isAssociationBranch = parentObjectTypeId && objectTypeId && parentObjectTypeId !== objectTypeId;
  const requiredAssociationInfo = isAssociationBranch ? {
    filterBranchType: 'ASSOCIATION',
    objectTypeId,
    operator: 'IN_LIST'
  } : {};
  return {
    filterBranch: Object.assign({
      filterBranchOperator: EnrollmentFilterBranchTypes.AND,
      // should always be an immutable object, but with some files still being in JS you can never be too careful
      filters: [filter.toJS ? filter.toJS() : filter],
      filterBranches: [],
      filterBranchType: EnrollmentFilterBranchTypes.AND
    }, requiredAssociationInfo)
  };
}
export function generateAssociatedListsUpdates(flow, enrollmentList, reenrollmentFilters) {
  const updates = [];

  // Create or update enrollment list.
  const existingEnrollmentList = getEnrollmentList(flow);
  if (existingEnrollmentList) {
    // If the given enrollmentList doesn't exist or is empty,
    // delete the ENROLLMENT_LIST entirely.
    if (!enrollmentList || !hasFilters(enrollmentList)) {
      updates.push({
        updateType: ApiPatchUpdateTypes.DELETE,
        listId: existingEnrollmentList.listId,
        listTypes: existingEnrollmentList.listTypes
      });
    } else {
      updates.push({
        updateType: ApiPatchUpdateTypes.UPDATE,
        filterBranch: enrollmentList,
        listId: existingEnrollmentList.listId,
        listTypes: existingEnrollmentList.listTypes
      });
    }
    // If the given enrollmentList doesn't exist or is empty,
    // don't create a new ENROLLMENT_LIST.
  } else if (enrollmentList && hasFilters(enrollmentList)) {
    /* generate initial list type based on classic listening setting */
    const listTypes = flow.getClassicListening() ? [ListTypes.ENROLLMENT_LIST, ListTypes.SUPPRESSION_REMOVE_LIST] : [ListTypes.ENROLLMENT_LIST];
    updates.push({
      updateType: ApiPatchUpdateTypes.CREATE,
      filterBranch: enrollmentList,
      listTypes
    });
  }
  const existingReenrollmentLists = getReenrollmentLists(flow);

  // Create all new re-enrollment filters.
  // Do nothing to existing filters.
  if (reenrollmentFilters) {
    reenrollmentFilters.forEach(filter => {
      const existingReenrollmentList = existingReenrollmentLists.find(list => {
        const filterBranch = list.filterBranch;
        return checkFilterEquality(filterBranch.filters[0], filter);
      });
      if (!existingReenrollmentList) {
        updates.push(Object.assign({}, buildAssociatedListFromFilter(filter, flow.get('objectTypeId')), {
          updateType: ApiPatchUpdateTypes.CREATE,
          listTypes: [getReenrollmentListType(filter)]
        }));
      }
    });

    // Delete all lingering re-enrollment filters.
    existingReenrollmentLists.forEach(list => {
      const existingFilter = list.filterBranch.filters[0];
      const correspondingReenrollmentFilter = reenrollmentFilters.find(reenrollmentFilter => checkFilterEquality(existingFilter, reenrollmentFilter));
      if (!correspondingReenrollmentFilter) {
        updates.push({
          updateType: ApiPatchUpdateTypes.DELETE,
          listId: list.listId,
          listTypes: [getReenrollmentListType(existingFilter)]
        });
      }
    });
  }
  return updates;
}
export function getCreatedAt(flow) {
  const createMetadata = getCreateMetadata(flow);
  if (!createMetadata) {
    return null;
  }
  return createMetadata.getUpdatedAt();
}
export function getSourceApp(flow) {
  if (!flow) {
    return '';
  }
  const {
    sourceApp = ''
  } = getCreateMetadata(flow) || {};
  return sourceApp;
}
export const isSourceAppSupportedInApp = flow => {
  if (!flow) {
    return false;
  }
  const sourceApp = getSourceApp(flow);
  return sourceApp ? !BLOCKLISTED_PLATFORM_SOURCE_APPS.includes(sourceApp) : true;
};
export const createActionIdMap = (flow, includeGoToEdges = true) => {
  return newCreateActionIdMap(flow.getActions(), includeGoToEdges);
};
export const createActionCardCounters = flow => {
  if (!flow) {
    return {};
  }
  return newCreateActionCardCounters(flow.getActions(), flow.getFirstActionId());
};
export const getActionHeaderTitle = ({
  flow,
  configSubType,
  extensionDefinitions,
  isUngated
}) => {
  if (!flow) {
    return () => null;
  }
  return newGetActionHeaderTitle({
    flowActions: flow.getActions(),
    firstActionId: flow.getFirstActionId(),
    configSubType,
    extensionDefinitions,
    isUngated: isUngated
  });
};
export const hydrateAction = ({
  flow,
  actionId
}) => {
  return hydrateMetadata({
    action: flow.getActionById(actionId)
  }).then(hydratedAction => flow.replaceAction(hydratedAction));
};
export const hydrateAllActions = ({
  flow,
  actionId: startingActionId = getFirstActionId(flow)
}) => {
  const actionIdsToHydrate = [startingActionId];
  const hydrationPromises = [];
  while (actionIdsToHydrate.length > 0) {
    const actionIdToHydrate = actionIdsToHydrate.pop();
    const actionToHydrate = flow.getActionById(actionIdToHydrate);
    hydrationPromises.push(hydrateMetadata({
      action: actionToHydrate
    }));
    if (!actionToHydrate.isLeafNode()) {
      for (const connector of actionToHydrate.getConnection().getConnectors()) {
        if (connector !== null && !connector.isGoToEdge()) {
          actionIdsToHydrate.push(connector.getNextActionId());
        }
      }
    }
  }

  // Once all the hydration requests have completed, update the action metadata in the flow and return it
  return Promise.all(hydrationPromises).then(hydratedActions => hydratedActions.reduce((newFlow, hydratedAction) => newFlow.replaceAction(hydratedAction), flow));
};
export function cloneHydratedActions(flow, clonedActionIds) {
  const actionsToClone = clonedActionIds.keySeq().map(clonedActionId => flow.getActionById(clonedActionId)).toArray();
  return fetchFixedDataPassingReferences(actionsToClone, clonedActionIds.toJS()).then(fixedActions => {
    return fixedActions.map(fixedActionJSON => {
      const originalAction = actionsToClone.find(({
        actionId
      }) => clonedActionIds.get(actionId) === fixedActionJSON.actionId);
      return new Action(fixedActionJSON).clearMetadata(originalAction);
    });
  });
}
export const findAllDecendantsActionIds = newFindAllDecendantsActionIds;
export const findAllAncestorsAndDecendantsOfAction = (flow, actionId, isNewAction) => {
  return newFindAllAncestorsAndDecendantsOfAction(flow.getActions(), actionId, isNewAction);
};
export const getActionOutputsForTargetActionById = ({
  flow,
  allOutputsByActionTypeId,
  instanceOutputsByActionId,
  targetActionId,
  isNewAction
}) => {
  return newGetActionOutputsForTargetActionById({
    actions: flow.getActions(),
    allOutputsByActionTypeId,
    instanceOutputsByActionId,
    targetActionId,
    isNewAction
  });
};

/**
 * @param {Flow} flow
 * @param {(number|string)} actionId - Number or one of SpecialActionIds.
 * @param {string} actionType - One of ActionTypes.
 * @param {object} [actionDefinition] - Extra options used by
 *    buildMetadataForAction. Currently only used by ExtensionActionMetadata.
 * @returns {(Action|null)} Returns the Action or null if the action doesn't
 *    exist or has the wrong type.
 */
export const getActionByIdAndType = (flow, actionId, actionType, actionDefinition) => {
  if (actionId === NEW_ACTION) {
    return Action.withExtraOpts({
      portalId: PortalIdParser.get(),
      flowId: getId(flow),
      actionType
    }, {
      actionDefinition
    });
  }
  const action = getActionById(flow, Number(actionId));
  if (!action) {
    return null;
  }
  return action.actionType === actionType ? action : null;
};
export const updateILSContactEnrollmentCriteria = ({
  flow,
  enrollmentList,
  flowEventFilters = null,
  reenrollmentFilters,
  shouldReenroll,
  enrollmentTrigger
}) => {
  const associatedListsUpdates = List(generateAssociatedListsUpdates(flow, enrollmentList, OrderedSet() // generate no ILS re-enrollment lists because we're using reEnrollmentTriggerSets
  ));
  const classicEnrollmentSettings = flow.getClassicEnrollmentSettings();
  const newClassicEnrollmentSettings = new ClassicEnrollmentSettings(Object.assign({}, classicEnrollmentSettings ? classicEnrollmentSettings.toJS() : {}, {
    enrollmentFilters: null,
    segmentCriteria: null,
    reEnrollmentTriggerSets: reenrollmentFilters,
    //sorry
    allowContactToTriggerMultipleTimes: shouldReenroll
  }));
  return flow.setClassicEnrollmentSettings(newClassicEnrollmentSettings).set('associatedLists', associatedListsUpdates).set('flowEventFilters', flowEventFilters).set('shouldReenroll', Boolean(shouldReenroll)).set('enrollmentTrigger', enrollmentTrigger || flow.enrollmentTrigger);
};
export const updateClassicEnrollmentCriteria = ({
  flow,
  enrollmentList,
  flowEventFilters,
  reenrollmentFilters,
  shouldReenroll,
  enrollmentTrigger,
  enrollmentSchedule
}) => {
  const classicEnrollmentSettings = flow.getClassicEnrollmentSettings();
  const newClassicEnrollmentSettings = new ClassicEnrollmentSettings(Object.assign({}, classicEnrollmentSettings ? classicEnrollmentSettings.toJS() : {}, {
    enrollmentFilters: null,
    segmentCriteria: enrollmentList,
    reEnrollmentTriggerSets: reenrollmentFilters,
    allowContactToTriggerMultipleTimes: shouldReenroll
  }));
  // If `enrollmentSchedule` is undefined, this was not an update to the enrollment schedule and it can be ignored.
  // Otherwise if `enrollmentSchedule` is `null` clear it, and if it's present update it.
  const newEnrollmentSchedule = enrollmentSchedule === undefined ? flow.getEnrollmentSchedule() : enrollmentSchedule ? new EnrollmentSchedule(enrollmentSchedule) : null;
  return flow.setClassicEnrollmentSettings(newClassicEnrollmentSettings).set('flowEventFilters', flowEventFilters || null).set('shouldReenroll', shouldReenroll).set('enrollmentTrigger', enrollmentTrigger || flow.enrollmentTrigger).set('enrollmentSchedule', newEnrollmentSchedule);
};
export const hasBranchActions = actions => {
  return actions.some(action => action.isBranch());
};
export function assignIdsToActionClones(flow, actionIdsToClone) {
  const nextActionId = flow.getNextAvailableActionId();
  return ImmutableMap(actionIdsToClone.valueSeq().filter(actionId => flow.actions.has(actionId)).map((actionId, index) => [actionId, nextActionId + index]));
}